// src/components/Finanzas/components/MetodosDonut.jsx
// Cobrado by payment method as a hand-rolled SVG donut + labelled legend.
//
// Takes v_finanzas_cobranza_mensual rows (or the same shape from finanzas_rango)
// as-is, plus the period total the server already computed. Slice size is each
// method's monto over that total — a display ratio, not a sum. Zero-value
// methods get no slice but stay in the legend at $0.00 so the legend never shifts.

import React from 'react';
import { fmtUsd, fmtPct, metodoMeta } from '../utils/facturaMath';
import { PAYMENT_METHOD_ORDER } from '../utils/paymentColors';

const SIZE = 180;
const R = 66;
const STROKE = 26;
const CIRC = 2 * Math.PI * R;

const MetodosDonut = ({ rows, total, periodLabel, loading, error, onRetry }) => {
  const byMetodo = new Map();
  for (const r of rows || []) byMetodo.set(r.metodo, r);

  // Fixed order first, then any unexpected method so it is never silently dropped.
  const metodos = [...PAYMENT_METHOD_ORDER];
  for (const key of byMetodo.keys()) if (!metodos.includes(key)) metodos.push(key);

  const totalNum = Number(total) || 0;
  const entries = metodos.map((metodo) => {
    const row = byMetodo.get(metodo);
    const monto = row ? row.monto : 0;
    const share = totalNum > 0 ? (Number(monto) || 0) / totalNum : 0;
    return { metodo, meta: metodoMeta(metodo), monto, pagos: row ? row.pagos : 0, share };
  });

  const slices = [];
  let offset = 0;
  for (const e of entries) {
    if (!(e.share > 0)) continue;
    const len = e.share * CIRC;
    slices.push({ ...e, len, offset });
    offset += len;
  }

  return (
    <div className="fin-card fin-chart">
      <div className="fin-card-label">Cobrado por método</div>
      <div className="fin-card-period">{periodLabel} · por fecha de pago</div>

      {error && (
        <div className="fin-banner fin-banner--error" role="alert">
          <span>{error}</span>
          {onRetry && <button type="button" className="fin-btn" onClick={onRetry}>Reintentar</button>}
        </div>
      )}
      {!error && loading && <div className="fin-muted">Cargando métodos de pago…</div>}
      {!error && !loading && (
        <div className="fin-donut-wrap">
          <svg className="fin-donut" viewBox={`0 0 ${SIZE} ${SIZE}`} role="img" aria-label={`Cobrado por método de pago — ${periodLabel}: ${fmtUsd(total)}`}>
            <circle className="fin-donut-track" cx={SIZE / 2} cy={SIZE / 2} r={R} fill="none" strokeWidth={STROKE} />
            <g transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}>
              {slices.map((s) => (
                <circle
                  key={s.metodo}
                  cx={SIZE / 2}
                  cy={SIZE / 2}
                  r={R}
                  fill="none"
                  stroke={s.meta.color}
                  strokeWidth={STROKE}
                  strokeDasharray={`${s.len} ${CIRC - s.len}`}
                  strokeDashoffset={-s.offset}
                >
                  <title>{`${s.meta.label}: ${fmtUsd(s.monto)} (${fmtPct(s.share)})`}</title>
                </circle>
              ))}
            </g>
            <text className="fin-donut-total" x={SIZE / 2} y={SIZE / 2 + 2} textAnchor="middle">{fmtUsd(total)}</text>
            <text className="fin-donut-caption" x={SIZE / 2} y={SIZE / 2 + 20} textAnchor="middle">
              {totalNum > 0 ? 'cobrado' : 'sin cobros'}
            </text>
          </svg>

          <ul className="fin-legend">
            {entries.map((e) => (
              <li key={e.metodo} className={`fin-legend-item ${e.share > 0 ? '' : 'is-empty'}`}>
                <span className="fin-legend-swatch" style={{ background: e.meta.color }} aria-hidden="true" />
                <span className="fin-legend-label">{e.meta.label}</span>
                <span className="fin-legend-amount">{fmtUsd(e.monto)}</span>
                <span className="fin-legend-pct">{fmtPct(e.share)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default MetodosDonut;
